import React, { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from './Button'
import { RADOM_COLORS } from '../util/Constants'
import { InputLabel, TextInputWithLabel, OptionalLabel, TextAreaWithLabel } from './Input'
import NumberInput from './NumberInput'
import PaymentPeriodSelector, { ProductPeriod } from './PaymentPeriodSelector'
import { IProductCreate, IProductUpdate } from '../state/Radom'
import SearchMultiselect from './SearchMultiselect'
import { Currencies } from '../util/Currencies'
import { errorToast } from '../util/Util'
import Checkbox from './Checkbox'
import Upload from '../icons/Upload'
import Subscription from '../icons/nav/Billing'
import Checkmark from '../icons/Checkmark'

const PageSectionTitle = styled.h2`
  margin: 0;
  color: ${RADOM_COLORS.BLACK};
  font-weight: 500;
  font-size: 16px;
`

const SectionHint = styled.div`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 13px;
  line-height: 1.5;
`

const FormShell = styled.div`
  font-size: 14px;
  width: 100%;
  min-width: 0;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  border-radius: 18px;
  border: 1px solid #eceff3;
  background: #fcfcfd;
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ResponsiveGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const SelectionGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const SelectableCard = styled.button<{ $selected?: boolean }>`
  align-items: flex-start;
  background: ${({ $selected }) => $selected ? '#f7f7f8' : '#fff'};
  border: 1px solid ${({ $selected }) => $selected ? '#d7dbe3' : '#eceff3'};
  border-radius: 16px;
  color: ${RADOM_COLORS.BLACK};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 92px;
  padding: 16px;
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease border-color;
  width: 100%;

  &:hover {
    background: ${({ $selected }) => $selected ? '#f7f7f8' : '#f8fafc'};
    border-color: ${({ $selected }) => $selected ? '#d7dbe3' : '#d8dee7'};
  }
`

const SelectableTitle = styled.span`
  color: ${RADOM_COLORS.BLACK};
  font-size: 14px;
  font-weight: 500;
`

const SelectableDescription = styled.span`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 12.5px;
  line-height: 1.45;
`

const SelectableIndicator = styled.div<{ $selected?: boolean }>`
  width: 100%;
  height: 3px;
  border-radius: 999px;
  margin-top: auto;
  background: ${({ $selected }) => $selected ? '#111111' : '#e7ebf1'};
`

const PricingCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  flex-grow: 1;
  border: 1px solid ${({ $selected }) => $selected ? '#d7dbe3' : '#eceff3'};
  background: ${({ $selected }) => $selected ? '#f7f7f8' : '#fff'};
  padding: 14px 16px;
  border-radius: 16px;
  gap: 14px;
  align-items: center;
  min-width: 0;
  cursor: pointer;
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease border-color;

  &:hover {
    background: ${({ $selected }) => $selected ? '#f7f7f8' : '#f8fafc'};
    border-color: ${({ $selected }) => $selected ? '#d7dbe3' : '#d8dee7'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const UploadRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 96px;
`

const StickyFooter = styled.div`
  display: flex;
  position: sticky;
  bottom: 0;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  border-top: 1px solid #eef1f5;
  padding: 18px 24px 0;

  @media (max-width: 720px) {
    flex-direction: column-reverse;
  }
`

export const ImageUploadButton = styled.div`
  cursor: pointer;
  border: 1px solid ${RADOM_COLORS.GRAY4};
  border-radius: 10px;
  width: 70px;
  height: 70px;
  border-style: dashed;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${RADOM_COLORS.GRAY_DARKER};
  transition: 0.2s ease all;
  position: relative;
  overflow: hidden;
  background-size: cover;

  :hover {
    border-color: ${RADOM_COLORS.GRAY_DARK};
  }

  :active {
    opacity: 0.5;
  }
`

export const ProductPeriodSecs = {
  Second: 1,
  Minute: 60,
  Hour: 60 * 60,
  Day: 60 * 60 * 24,
  Week: 60 * 60 * 24 * 7,
  Month: 60 * 60 * 24 * 30,
  Quarter: 60 * 60 * 24 * 30 * 3,
  SixMonths: 60 * 60 * 24 * 30 * 6,
  Year: 60 * 60 * 24 * 365
}

const ProductPeriodDisplay: { [key in ProductPeriod]: string } = {
  [ProductPeriod.Day]: 'Day',
  [ProductPeriod.Week]: 'Week',
  [ProductPeriod.Month]: 'Month',
  [ProductPeriod.Quarter]: 'Quarter',
  [ProductPeriod.SixMonths]: 'Six Months',
  [ProductPeriod.Year]: 'Year'
}

export interface IProductUpdateV2 extends IProductUpdate {
  image: File
}

interface IProps {
  form: IProductCreate
  isSubmitting: boolean
  isLoading: boolean
  modal?: boolean
  imageUrl?: string
  disableRecurring?: boolean
  disabled?: boolean
  update?: boolean
  sendEmailNotifications?: boolean
  allowedProductTypes?: ProductType[]
  onCancel?: () => void
  onFormUpdate: (form: Partial<IProductUpdateV2>) => void
  onFormSubmit: () => void
}

export enum ProductType {
  OneTime = 'One time',
  Subscription = 'Subscription',
  Presale = 'Presale'
}

interface ProductCategoryProps {
  name: string
  description: string
  isSelected: boolean
  onClick: () => void
}

const ProductCategory = (props: ProductCategoryProps): ReactElement => {
  return <SelectableCard
    type='button'
    onClick={props.onClick}
    $selected={props.isSelected}>
    <SelectableTitle>{props.name}</SelectableTitle>
    <SelectableDescription>{props.description}</SelectableDescription>
    <SelectableIndicator $selected={props.isSelected} />
  </SelectableCard>
}

interface PricingTypeProps {
  isOnetime?: boolean
  isSelected: boolean
  disabled?: boolean
  onClick: () => void
}

const PricingType = (props: PricingTypeProps): ReactElement => {
  return <PricingCard
    onClick={props.onClick}
    type='button'
    disabled={props.disabled}
    $selected={props.isSelected}>

    {
      props.isOnetime
        ? <Checkmark
          style={{ width: 15, height: 15 }}
          stroke={props.isSelected ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY4} />
        : <Subscription
          style={{ width: 18, height: 18 }}
          stroke={props.isSelected ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY4}
          fill={props.isSelected ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY4} />
    }

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      color: RADOM_COLORS.GRAY4,
      alignItems: 'flex-start',
      gap: 4
    }}>
      <span style={{
        fontSize: 14,
        fontWeight: 500,
        color: props.isSelected ? RADOM_COLORS.BLACK : RADOM_COLORS.GRAY4
      }}>
        {props.isOnetime ? 'One-time' : 'Subscription'}
      </span>
      <span style={{ fontSize: 12.5, color: RADOM_COLORS.GRAY_DARKER }}>
        {props.isOnetime ? 'Charge a one time fee' : 'Charge an ongoing fee'}
      </span>
    </div>

  </PricingCard>
}

const ProductForm = (props: IProps): ReactElement => {
  const imageFileInput = useRef<HTMLInputElement>(null)
  const [imgUrl, setImgUrl] = useState<string>()
  const [productType, setProductType] = useState<ProductType>(
    props.allowedProductTypes ? props.allowedProductTypes[0] : ProductType.OneTime
  )
  const [period, setPeriod] = useState(ProductPeriod.Month)
  const productTypes = props.allowedProductTypes ?? [ProductType.OneTime, ProductType.Subscription, ProductType.Presale]

  const disabled = (): boolean => {
    return props.isLoading || props.isSubmitting
  }

  const onImageFileChange = async (): Promise<void> => {
    const fileList = imageFileInput.current?.files
    if (fileList && fileList.length > 0) {
      const imageFile = fileList[0]
      if (imageFile.size > 4000000) {
        errorToast('File size must be less than 4MB')
      }
      setImgUrl(URL.createObjectURL(imageFile))

      props.onFormUpdate({ image: fileList[0] })
    }
  }

  const onFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    e.stopPropagation()

    if (!props.form.name || props.form.name.trim() === '') {
      errorToast('Product name is required.')
      return
    }

    if (!props.form.price || Number(props.form.price) <= 0) {
      errorToast('Price is required and must be greater than zero.')
      return
    }

    props.onFormSubmit()
  }

  useEffect(() => {
    if (productType === ProductType.Subscription) {
      props.onFormUpdate({
        chargingIntervalSeconds: ProductPeriodSecs[period]
      })
    } else {
      props.onFormUpdate({
        chargingIntervalSeconds: undefined
      })
    }
  }, [productType])

  useEffect(() => {
    if (props.form.chargingIntervalSeconds && props.form.chargingIntervalSeconds > 0) {
      setProductType(ProductType.Subscription)
      setPeriod(Object.entries(ProductPeriodSecs)
        .find(p => p[1] === props.form.chargingIntervalSeconds)?.[0] as ProductPeriod ??
        ProductPeriod.Month
      )
    }

    if (props.form.productType?.Presale) {
      setProductType(ProductType.Presale)
    }
  }, [props.form])

  useEffect(() => {
    if (props.imageUrl) {
      setImgUrl(props.imageUrl)
    }
  }, [props.imageUrl])

  return <FormShell>
    <form onSubmit={onFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {
          !props.update &&
          productTypes.includes(ProductType.Presale) &&
          productTypes.includes(ProductType.OneTime) &&
          <FormSection>
            <SectionHeader>
              <PageSectionTitle>Product type</PageSectionTitle>
              <SectionHint>Choose whether you are selling a regular product or a token sale.</SectionHint>
            </SectionHeader>

            <SelectionGrid>
              <ProductCategory
                onClick={() => setProductType(ProductType.OneTime)}
                name='Good / Service'
                description='One-time products or subscriptions'
                isSelected={productType !== ProductType.Presale} />

              <ProductCategory
                onClick={() => setProductType(ProductType.Presale)}
                name='Token'
                description='Sell a digital token'
                isSelected={productType === ProductType.Presale} />
            </SelectionGrid>
          </FormSection>
        }

        {
          productType !== 'Presale' &&
          <FormSection>
            <SectionHeader>
              <PageSectionTitle>Product details</PageSectionTitle>
              <SectionHint>Set the name, description, and optional visual that merchants and customers will see.</SectionHint>
            </SectionHeader>

            <TextInputWithLabel
              disabled={disabled()}
              label='Name'
              value={props.form.name}
              placeholder='Pro subscription plan, sneakers, etc.'
              onChange={e => props.onFormUpdate({ name: (e.target as HTMLInputElement).value })}
              required
            />

            <ResponsiveGrid>
              <div style={{ minWidth: 0 }}>
                <TextAreaWithLabel
                  style={{ maxHeight: 160 }}
                  disabled={disabled()}
                  label='Description'
                  value={props.form.description}
                  placeholder='Add a short summary for your team or customers.'
                  onChange={e => props.onFormUpdate({ description: (e.target as HTMLInputElement).value })}
                />
              </div>
              <UploadRail>
                <div style={{ display: 'flex', gap: 5 }}>
                  <InputLabel>Image</InputLabel>
                  <OptionalLabel>Optional</OptionalLabel>
                </div>
                <ImageUploadButton style={{ width: 86, height: 86 }}>
                  {
                    !imgUrl &&
                     <div style={{
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       pointerEvents: 'none',
                       gap: 4,
                       color: RADOM_COLORS.BLACK
                     }}>
                       <Upload style={{ height: 25 }} />
                       <span style={{ fontSize: 11 }}>Upload</span>
                     </div>
                  }
                  <input
                    disabled={disabled()}
                    type="file"
                    name="profile_picture"
                    ref={imageFileInput}
                    onChange={async () => await onImageFileChange()}
                    style={{
                      opacity: 0,
                      cursor: 'pointer',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0
                    }}
                  />
                  {
                    imgUrl &&
                    <img src={imgUrl} style={{
                      pointerEvents: 'none',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      position: 'absolute'
                    }} />
                  }
                </ImageUploadButton>
              </UploadRail>
            </ResponsiveGrid>
          </FormSection>
        }

        {
          productType === ProductType.Presale &&
          <FormSection>
            <SectionHeader>
              <PageSectionTitle>Token details</PageSectionTitle>
              <SectionHint>Capture the token identity and icon that will appear across the checkout flow.</SectionHint>
            </SectionHeader>

            <ResponsiveGrid>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 20, minWidth: 0 }}>
                <TextInputWithLabel
                  disabled={disabled()}
                  label='Token name'
                  value={props.form.name}
                  placeholder='Uniswap'
                  onChange={e => props.onFormUpdate({ name: (e.target as HTMLInputElement).value })}
                  required
                />

                <TextInputWithLabel
                  disabled={disabled()}
                  label='Token ticker'
                  value={props.form.productType?.Presale?.Token?.ticker}
                  placeholder='UNI'
                  onChange={e => props.onFormUpdate({
                    productType: {
                      Presale: {
                        Token: {
                          ...props.form.productType?.Presale?.Token,
                          ticker: (e.target as HTMLInputElement).value
                        }
                      }
                    }
                  })}
                  required
                />

                <TextInputWithLabel
                  disabled={disabled()}
                  label='Token decimals'
                  value={props.form.productType?.Presale?.Token?.decimals}
                  placeholder='6'
                  onChange={(e: any) => props.onFormUpdate({
                    productType: {
                      Presale: {
                        Token: {
                          ...props.form.productType?.Presale?.Token,
                          decimals: isNaN(e.target.value) || !e.target.value ? '' : Number(e.target.value)
                        }
                      }
                    }
                  })}
                  required
                />
              </div>

              <UploadRail>
                <div style={{ display: 'flex', gap: 5 }}>
                  <InputLabel>Token icon</InputLabel>
                </div>
                <ImageUploadButton style={{ width: 86, height: 86 }}>
                  {
                    !imgUrl &&
                     <div style={{
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       pointerEvents: 'none',
                       gap: 4,
                       color: RADOM_COLORS.BLACK
                     }}>
                       <Upload style={{ height: 25 }} />
                       <span style={{ fontSize: 11 }}>Upload</span>
                     </div>
                  }
                  <input
                    disabled={disabled()}
                    type="file"
                    name="profile_picture"
                    ref={imageFileInput}
                    onChange={async () => await onImageFileChange()}
                    style={{
                      opacity: 0,
                      cursor: 'pointer',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0
                    }}
                  />
                  {
                    imgUrl &&
                    <img src={imgUrl} style={{
                      pointerEvents: 'none',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      position: 'absolute'
                    }} />
                  }
                </ImageUploadButton>
              </UploadRail>
            </ResponsiveGrid>
          </FormSection>
        }

        {
          productType === ProductType.Presale &&
          <FormSection>
            <SectionHeader>
              <PageSectionTitle>Token pricing</PageSectionTitle>
              <SectionHint>Choose the settlement currency and price shown at checkout.</SectionHint>
            </SectionHeader>

            <ResponsiveGrid>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <InputLabel>Currency</InputLabel>
                <SearchMultiselect
                  disabled={props.update ?? disabled()}
                  placeholder="Select currency"
                  itemArray={Currencies}
                  selectedItems={Currencies.filter(c => c.ticker === props.form.currency) ?? []}
                  setSelectedItems={c => props.onFormUpdate({ currency: c[0]?.ticker })}
                  keyExtractor={c => c.ticker}
                  labelExtractor={c => <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{c.icon}{c.ticker}</div>}
                  outerStyle={{ minHeight: 32, alignItems: 'center' }}
                  isSingleSelect
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexGrow: 1 }}>
                <InputLabel>Token price</InputLabel>
                <NumberInput
                  disabled={disabled() || props.update}
                  value={props.form.price}
                  onUserInput={(e) => props.onFormUpdate({ price: e as any })}
                  fontSize='14'
                  align='right'
                  required
                />
              </div>
            </ResponsiveGrid>
          </FormSection>
        }

        {
          productType !== ProductType.Presale &&
          <FormSection>
            <SectionHeader>
              <PageSectionTitle>Product pricing</PageSectionTitle>
              <SectionHint>Choose whether customers pay once or on a recurring schedule.</SectionHint>
            </SectionHeader>

            <SelectionGrid>
              <PricingType
                disabled={props.update ?? disabled()}
                isOnetime
                isSelected={productType === ProductType.OneTime}
                onClick={() => setProductType(ProductType.OneTime)} />
              <PricingType
                disabled={props.update ?? disabled()}
                isSelected={productType === ProductType.Subscription}
                onClick={() => setProductType(ProductType.Subscription)} />
            </SelectionGrid>

            {
              productType === ProductType.Subscription &&
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InputLabel>Subscription management</InputLabel>
                <SecondaryButton
                  type='button'
                  onClick={() =>
                    props.onFormUpdate({ sendSubscriptionEmails: !props.form.sendSubscriptionEmails })
                  }
                  style={{
                    border: '1px solid #eceff3',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '0 14px',
                    color: 'black',
                    minHeight: 44
                  }}>
                  <Checkbox
                    size={16}
                    checked={!!props.form.sendSubscriptionEmails} />
                  <InputLabel style={{ opacity: 1 }}>Send customer email payment reminders</InputLabel>
                </SecondaryButton>
              </div>
            }
            {
              productType === ProductType.Subscription &&
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <PaymentPeriodSelector
                  label
                  period={period}
                  disabled={props.update}
                  setPeriod={p => props.onFormUpdate({
                    chargingIntervalSeconds: ProductPeriodSecs[p as string]
                  })}
                />
              </div>
            }
            <ResponsiveGrid>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <InputLabel>Currency</InputLabel>
                <SearchMultiselect
                  disabled={props.update ?? disabled()}
                  placeholder="Select currency"
                  itemArray={Currencies}
                  selectedItems={Currencies.filter(c => c.ticker === props.form.currency) ?? []}
                  setSelectedItems={c => props.onFormUpdate({ currency: c[0]?.ticker })}
                  keyExtractor={c => c.ticker}
                  labelExtractor={c => <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{c.icon}{c.ticker}</div>}
                  outerStyle={{ minHeight: 32, alignItems: 'center' }}
                  isSingleSelect
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexGrow: 1 }}>
                {
                  productType === ProductType.Subscription &&
                    <InputLabel>Price per {ProductPeriodDisplay[period].toLowerCase()}</InputLabel>
                }
                {productType !== ProductType.Subscription && <InputLabel>Price</InputLabel>}
                <NumberInput
                  disabled={disabled() || props.update}
                  value={props.form.price}
                  onUserInput={(e) => props.onFormUpdate({ price: e as any })}
                  fontSize='14'
                  align='right'
                  required
                />
              </div>
            </ResponsiveGrid>
          </FormSection>
        }

      </div>

      <StickyFooter>
        {
          props.update && <SecondaryButton
            disabled={disabled()}
            onClick={props.onCancel}
            style={{ width: '100%' }}>
              Cancel
          </SecondaryButton>
        }
        <PrimaryButton
          isLoading={ props.isLoading || props.isSubmitting }
          disabled={ disabled() || (props.disabled ?? false) }
          onClick={onFormSubmit}
          style={{ width: '100%' }}>
          { props.update ? 'Update' : 'Create' }
        </PrimaryButton>
      </StickyFooter>
    </form>
  </FormShell>
}

export default ProductForm
